import stringify from 'json-stable-stringify'
import at from '../actionTypes'
import createReducer from './utilities/createReducer'
import that from '../../utilities/translationHelper'
import icons from '../../enums/icons'
import voteTypes from '../../enums/voteTypes'
import maybe from '../../utilities/maybe'
import arrayMove from '../../utilities/arrayMove'
import showAppMessage from './utilities/showAppMessage'
import objToArray from '../../utilities/objToArray'
import Item from '../../utilities/Data/Item'

const untitledKitName = that.context.translate('Untitled kit')

const getKitDetails = (myKit) => {
	const kit = { ...myKit }

	//	Grab the right colour itemImages, and add to the gallery and item
	if (kit && kit.items) {
		kit.items.forEach((kitItem, idx) => {
			const images = Item.getImages(kitItem.item, kitItem)
			kit.items[idx].itemImages = images
		})
	}

	const kitDraft = {
		...kit,
		algoliaProcessingRequired: true
	}

	return {
		kit,
		kitDraftAsLoaded: stringify(kitDraft),
		kitDraft
	}
}

//	Map an Algolia search response item to a kit Item object
const algoliaItemToKitItem = aItem => ({
	colourName: aItem.colour.colourName,
	itemImages: objToArray(aItem.colour.images),
	itemKey: aItem.itemKey,
	item: {
		brandKey: aItem.brandKey,
		brandName: aItem.brandName,
		itemKey: aItem.itemKey,
		itemName: aItem.itemName,
		itemType: aItem.itemType,
		colours: {
			0: {
				colourName: aItem.colour.colourName,
				images: objToArray(aItem.colour.images)
			}
		}
	}
})

const itemToKitItem = item => ({
	colourName: item.colourName,
	itemImages: Item.getImages(item),
	itemKey: item.itemKey,
	item: {
		brandKey: item.brandKey,
		brandName: item.brandName,
		itemKey: item.itemKey,
		itemName: item.itemName,
		itemType: item.itemType,
		colours: {
			0: {
				colourName: item.colourName,
				images: Item.getImages(item)
			}
		}
	}
})

const addKitItemToKitDraft = (state, kitItem, includeKit) => {
	const maxItems = 15
	const addedItems = state.kitDraft && state.kitDraft.items ? state.kitDraft.items.length : 0
	const tooManyItems = addedItems >= maxItems

	//	Add a tag for the item
	const kitTag = {
		text: `${kitItem.item.brandName} ${kitItem.item.itemName}`,
		type: 'item',
		itemKey: kitItem.itemKey,
		arrowDown: !!(kitItem.arrowDown),
		colourName: kitItem.colourName,
		x: null,
		y: null
	}

	const kitDraft = state.kitDraft
	//	Make sure we only add an item once
	const hasItem = kitDraft && kitDraft.items && kitDraft.items.filter(item => Item.isSameItem(item, kitItem)).length > 0
	const shallAddItem = !hasItem && !tooManyItems

	const result = {
		...state,
		...(tooManyItems ? showAppMessage(that.context.translate('Cannot add item - you\'ve added too many items to this kit, maximum {maxItems}', { maxItems })) : []),
		kitDraft: {
			...kitDraft,
			items: [...(kitDraft && kitDraft.items ? [...kitDraft.items] : []), ...(shallAddItem ? [{ ...kitItem }] : [])],
			tags: [...(kitDraft && kitDraft.tags ? [...kitDraft.tags] : []), ...(shallAddItem ? [{ ...kitTag }] : [])]
		}
	}

	if (includeKit) {
		const kit = result.kit
		result.kit = {
			...result.kit,
			items: [...(kit && kit.items ? [...kit.items] : []), ...(shallAddItem ? [{ ...kitItem }] : [])],
			tags: [...(kit && kit.tags ? [...kit.tags] : []), ...(shallAddItem ? [{ ...kitTag }] : [])]
		}
	}

	return result
}

export default createReducer({}, {
	[at.kitDraftCreate]: (state, action) => ({
		...state,
		kitDraft: {
			...((action && action.payload && action.payload.kit) || {
				name: untitledKitName,
				items: [],
				tags: [],
				...state.kitDraft
			}),
			algoliaProcessingRequired: true
		}
	}),

	[at.kitDraftClear]: state => ({
		...state,
		kitDraftAsLoaded: null,
		kitDraft: {
			algoliaProcessingRequired: true,
			name: untitledKitName,
			items: [],
			tags: []
		}
	}),

	[at.kitDraftItemDelete]: (state, action) => ({
		...state,
		kitDraft: {
			...state.kitDraft,
			items: [
				...state.kitDraft.items.filter(item => !(Item.isSameItem(item, action.payload)))
			],
			tags: [
				...state.kitDraft.tags.filter(tag => !(Item.isSameItem(tag, action.payload)))
			]
		}
	}),

	//	TODO: Actually also go and delete the suggestions that are filtered out...
	[at.kitSuggestionDelete_Begin]: (state, action) => ({
		...state,
		kitSuggestions: state.kitSuggestions ? state.kitSuggestions.filter(item => !(Item.isSameItem(item, action.payload))) : [],
		addedKitSuggestions: state.addedKitSuggestions ? state.addedKitSuggestions.filter(item => !(Item.isSameItem(item, action.payload))) : []
	}),

	[at.kitDraftSelectItem]: (state, action) => {
		// accept either a real item or an algolia item
		const kitItem = (action.payload.item && itemToKitItem(action.payload.item)) || algoliaItemToKitItem(action.payload.aItem)
		return addKitItemToKitDraft(state, kitItem)
	},

	[at.kitSuggestItem]: (state, action) => {
		const maxSuggestions = 100
		const suggestionsCount = state.kitSuggestions ? state.kitSuggestions.length : 0
		const tooManyItems = suggestionsCount >= maxSuggestions
		const kitDraft = state.kitDraft
		const kitItem = (action.payload.item && itemToKitItem(action.payload.item)) || algoliaItemToKitItem(action.payload.aItem)
		//	Make sure we only add an item once
		const hasItem = kitDraft && kitDraft.items && kitDraft.items.filter(item => Item.isSameItem(item, kitItem)).length > 0
		const hasSuggestedItem = kitDraft && state.kitSuggestions && state.kitSuggestions.filter(item => Item.isSameItem(item, kitItem)).length > 0
		const shallAddItem = !hasItem && !hasSuggestedItem && !tooManyItems
		const kitSuggestions = state.kitSuggestions ? [...state.kitSuggestions] : []
		// const addedKitSuggestions = state.addedKitSuggestions || []
		const addedKitSuggestions = state.addedKitSuggestions ? [...state.addedKitSuggestions] : []

		//	If we're adding the item, also optimistically update kitSuggestions
		if (shallAddItem) {
			const suggestedItem = {
				...kitItem,
				uid: state.user.uid,
				userVoteType: voteTypes.UP.type,
				vote: {
					[voteTypes.UP.type]: 1,
					score: 1
				},
				userName: state.user.userProfile.userName
			}
			kitSuggestions.push(suggestedItem)
			addedKitSuggestions.push(suggestedItem)
		}

		return {
			...state,
			...(tooManyItems ? showAppMessage(that.context.translate('Cannot suggest item - maximum suggestions {maxSuggestions}', { maxSuggestions })) : []),
			kitSuggestions,
			addedKitSuggestions
		}
	},

	[at.kitDraftLoadedAllUserKits]: (state, action) => ({
		...state,
		hasLoadedAllUserKits: true,
		userKitCount: action.payload.userKitCount
	}),

	[at.kitSuggestionAddToDraft]: (state, action) => addKitItemToKitDraft(state, action.payload.item, true),

	[at.kitDraftSetTags]: (state, action) => ({
		...state,
		kitDraft: {
			...state.kitDraft,
			tags: action.payload.tags
		}
	}),

	[at.kitDraftFetch]: state => ({
		...state,
		kitDraft: undefined
	}),

	[at.kitDraftFetch_Success]: (state, action) => ({
		...state,
		...getKitDetails(action.payload.kit)
	}),

	[at.kitDraftTemplateUpdate]: (state, action) => ({
		...state,
		kitDraft: {
			...state.kitDraft,
			templateSettings: action.payload.templateSettings
		}
	}),

	[at.kitDraftSetImageUrlAndKitId]: (state, action) => ({
		...state,
		kitDraft: {
			...state.kitDraft,
			kitId: action.payload.kitId,
			images: undefined,
			imageUrl: action.payload.imageUrl
		}
	}),

	[at.kitDraftItemsSort]: (state, action) => {
		const { oldIndex, newIndex } = action.payload
		const items = arrayMove(Object.assign([], state.kitDraft.items), oldIndex, newIndex)
		const tags = arrayMove(Object.assign([], state.kitDraft.tags), oldIndex, newIndex)
		return {
			...state,
			kitDraft: {
				...state.kitDraft,
				items,
				tags
			}
		}
	},

	[at.kitDraftSet]: (state, action) => {
		const kitDraft = action.payload.kitDraft
		let kitName = kitDraft.name
		//	If we have an untitled kit, derive the most appropriate name based on item count
		if (kitDraft.name === untitledKitName && kitDraft.items) {
			const names = {}
			kitDraft.items.forEach((kItem) => {
				names[kItem.item.brandName] = names[kItem.item.brandName] || 0
				names[kItem.item.brandName] += 1
			})
			let max = 0
			Object.keys(names).forEach((name) => {
				if (names[name] > max) {
					kitName = `${name} kit`
					max = names[name]
				}
			})
		}
		return {
			...state,
			...maybe({ kit: { ...state.kit, name: kitName } }, state.kit),
			kitDraft: {
				...kitDraft,
				name: kitName
			}
		}
	},

	[at.kitDraftSet_Success]: (state, action) => ({
		...state,
		postKitUserKitsHasMoreKits: true,
		// optimistically set the kit
		kit: {
			...action.payload.kitDraft,
			kitId: action.payload.kitId
		}
	}),

	[at.kitDraftSetFromKit]: (state, action) => ({
		...state,
		...getKitDetails(action.payload.kit),
		heading: {
			text: that.context.translate('Log/post ride in {kitName}', { kitName: action.payload.kit.name }),
			icon: icons.whiteKit
		}
	}),

	[at.kitDraftSetImages]: (state, action) => ({
		...state,
		kitDraft: {
			...state.kitDraft,
			images: action.payload.images
		}
	}),

	[at.kitFetch]: (state, action) => {
		const newState = {
			...state,
			heading: {
				text: that.context.translate('Loading...'),
				icon: undefined
			}
		}

		delete newState.kit
		delete newState.numKitItems

		if (action && action.payload && newState.kitDraft && action.payload.kitId !== undefined && action.payload.kitId !== 'undefined' && action.payload.kitId === newState.kitDraft.kitId) {
			newState.kit = { ...newState.kitDraft }
		} else {
			delete newState.kitDraft
		}

		return newState
	},

	[at.kitFetch_Success]: (state, action) => ({
		...state,
		...getKitDetails(action.payload.kit),
		heading: {
			text: `${action.payload.kit.name}`,
			icon: icons.whiteKit
		}
	}),

	[at.kitShareFetch]: (state) => {
		const newState = {
			...state
		}

		delete newState.kitForSharing
		return newState
	},

	[at.kitShareFetch_Success]: (state, action) => ({
		...state,
		kitForSharing: getKitDetails(action.payload.kit).kit
	}),

	[at.kitPostsFetch_Success]: (state, action) => ({
		...state,
		kitPosts: action.payload.posts
	}),

	[at.kitSuggestionsFetch_Success]: (state, action) => {
		const suggestions = action.payload.suggestions.reduce((result, suggestion) => {
			let itemSuggestions = Object.keys(suggestion.items).map(itemKey => ({ ...suggestion.items[itemKey], userName: suggestion.userName, uid: suggestion.uid }))

			itemSuggestions = itemSuggestions.map((iSuggestion) => {
				const images = Item.getImages(iSuggestion.item, iSuggestion)
				iSuggestion.itemImages = images // eslint-disable-line no-param-reassign
				return iSuggestion
			})

			return result.concat(itemSuggestions)
		}, [])

		const resultObj = {}

		suggestions.forEach((suggestion) => {
			const key = `${suggestion.itemKey}-${suggestion.colourName}`
			resultObj[key] = resultObj[key] || { ...suggestion }

			resultObj[key].vote = resultObj[key].vote || {}

			resultObj[key].vote[suggestion.voteType] = resultObj[key].vote[suggestion.voteType] || 0
			resultObj[key].vote[suggestion.voteType] += 1

			resultObj[key].vote.score = resultObj[key].vote.score || 0
			if (suggestion.voteType === voteTypes.UP.type) {
				resultObj[key].vote.score += 1
			} else if (suggestion.voteType === voteTypes.DOWN.type) {
				resultObj[key].vote.score -= 1
			}

			resultObj[key].createdTimestamp = Math.min(resultObj[key].createdTimestamp || 9999999999999, suggestion.createdTimestamp)
			if (suggestion.createdTimestamp === resultObj[key].createdTimestamp) {
				resultObj[key].userName = suggestion.userName
			}
			//	User the current uid to see if they have voted on this suggestion
			delete resultObj[key].voteType
			if (state.user.uid === suggestion.uid) {
				resultObj[key].userVoteType = suggestion.voteType
			}
		})
		const kitSuggestions = objToArray(resultObj)
		//	Sort by vote score, then createdTimestamp
		kitSuggestions.sort((a, b) => {
			const scoreOrder = b.vote.score - a.vote.score
			return scoreOrder !== 0 ? scoreOrder : b.createdTimestamp - a.createdTimestamp
		})

		return ({
			...state,
			kitSuggestions
		})
	},

	//	Optimistic update of suggestion votes
	[at.kitSuggestionSet_Begin]: (state, action) => (state.kitSuggestions ? ({
		...state,
		kitSuggestions: state.kitSuggestions.map((suggestion) => {
			const nSuggestion = { ...suggestion }
			const pSuggestion = action.payload.kitSuggestion
			if (pSuggestion.itemKey === nSuggestion.itemKey) {
				let hasModifiedVoteScore = false
				nSuggestion.vote = nSuggestion.vote || {}
				nSuggestion.vote.UP = nSuggestion.vote.UP || 0
				nSuggestion.vote.NEUTRAL = nSuggestion.vote.NEUTRAL || 0
				nSuggestion.vote.DOWN = nSuggestion.vote.DOWN || 0
				nSuggestion.vote.score = nSuggestion.vote.score || 0

				//	If the users vote was previously set to UP
				if (nSuggestion.userVoteType === voteTypes.UP.type) {
					nSuggestion.vote.UP -= 1
					if (pSuggestion.voteType === voteTypes.NEUTRAL.type) {
						nSuggestion.vote.score -= 1
						nSuggestion.vote.NEUTRAL += 1
					} else if (pSuggestion.voteType === voteTypes.DOWN.type) {
						nSuggestion.vote.score -= 2
						nSuggestion.vote.DOWN += 1
					}
					hasModifiedVoteScore = true
				}

				//	If the users vote was previously set to NEUTRAL
				if (nSuggestion.userVoteType === voteTypes.NEUTRAL.type) {
					nSuggestion.vote.NEUTRAL -= 1
					if (pSuggestion.voteType === voteTypes.UP.type) {
						nSuggestion.vote.score += 1
						nSuggestion.vote.UP += 1
					} else if (pSuggestion.voteType === voteTypes.DOWN.type) {
						nSuggestion.vote.score -= 1
						nSuggestion.vote.DOWN += 1
					}
					hasModifiedVoteScore = true
				}

				//	If the users vote was previously set to DOWN
				if (nSuggestion.userVoteType === voteTypes.DOWN.type) {
					nSuggestion.vote.DOWN -= 1
					if (pSuggestion.voteType === voteTypes.UP.type) {
						nSuggestion.vote.score += 2
						nSuggestion.vote.UP += 1
					} else if (pSuggestion.voteType === voteTypes.NEUTRAL.type) {
						nSuggestion.vote.score += 1
						nSuggestion.vote.NEUTRAL += 1
					}
					hasModifiedVoteScore = true
				}

				nSuggestion.userVoteType = pSuggestion.voteType
				if (!hasModifiedVoteScore) {
					// Need to update the score
					nSuggestion.vote = nSuggestion.vote || {}
					nSuggestion.vote[pSuggestion.voteType] = nSuggestion.vote[pSuggestion.voteType] || 0
					nSuggestion.vote[pSuggestion.voteType] += 1
				}
			}

			return nSuggestion
		})
	}) : state),

	[at.kitSuggestionSet_Success]: (state) => {
		const result = { ...state }
		delete result.addedKitSuggestions
		return result
	},

	[at.kitOwnSet_Success]: (state, action) => {
		if (state.kit && state.kit.kitId === action.payload.kit.kitId) {
			let countOwned = state.kit.countOwned || 0
			if (action.payload.own !== state.kit.own) {
				countOwned += action.payload.own ? 1 : -1
			}
			countOwned = countOwned > 0 ? countOwned : 0
			return ({
				...state,
				kit: {
					...state.kit,
					countOwned
				}
			})
		}
		return state
	},

	[at.kitWantSet_Success]: (state, action) => {
		if (state.kit && state.kit.kitId === action.payload.kit.kitId) {
			let countWanted = state.kit.countWanted || 0
			if (action.payload.want !== state.kit.want) {
				countWanted += action.payload.want ? 1 : -1
			}
			countWanted = countWanted > 0 ? countWanted : 0
			return ({
				...state,
				kit: {
					...state.kit,
					countWanted
				}
			})
		}
		return state
	},

	[at.kitOwnSet_Fail]: state => (state.kit ? ({
		...state,
		kit: {
			...state.kit // re-set the same kit - so the kit (the OwnWant count particularly) re-renders (resetting the optimistic UI update)
		}
	}) : state),
	[at.kitWantSet_Fail]: state => (state.kit ? ({
		...state,
		kit: {
			...state.kit
		}
	}) : state)

})



// WEBPACK FOOTER //
// ./src/redux/reducers/kitReducer.js